import React from 'react';
import { TrendingUp, TrendingDown, Users, Calendar, DollarSign, Activity } from 'lucide-react';
import './AnalyticsCard.css';

const AnalyticsCard = () => {
  const chartData = [
    { month: 'Jan', patients: 120, revenue: 45000, appointments: 180 },
    { month: 'Feb', patients: 135, revenue: 52000, appointments: 195 },
    { month: 'Mar', patients: 150, revenue: 58000, appointments: 210 },
    { month: 'Apr', patients: 140, revenue: 54000, appointments: 200 },
    { month: 'May', patients: 165, revenue: 62000, appointments: 225 },
    { month: 'Jun', patients: 180, revenue: 68000, appointments: 240 },
    { month: 'Jul', patients: 175, revenue: 65000, appointments: 235 },
    { month: 'Aug', patients: 190, revenue: 72000, appointments: 250 },
    { month: 'Sep', patients: 200, revenue: 75000, appointments: 260 },
    { month: 'Oct', patients: 185, revenue: 70000, appointments: 245 },
    { month: 'Nov', patients: 210, revenue: 78000, appointments: 270 },
    { month: 'Dec', patients: 225, revenue: 85000, appointments: 285 }
  ];

  const metrics = [
    {
      title: 'Total Revenue',
      value: '$850,000',
      change: '+12.5%',
      trend: 'up',
      icon: <DollarSign size={24} />,
      color: '#28a745'
    },
    {
      title: 'New Patients',
      value: '2,847',
      change: '+8.2%',
      trend: 'up',
      icon: <Users size={24} />,
      color: '#17a2b8'
    },
    {
      title: 'Appointments',
      value: '3,420',
      change: '+15.3%',
      trend: 'up',
      icon: <Calendar size={24} />,
      color: '#DAA520'
    },
    {
      title: 'Conversion Rate',
      value: '68.5%',
      change: '-2.1%',
      trend: 'down',
      icon: <Activity size={24} />,
      color: '#dc3545'
    }
  ];

  const topServices = [
    { name: 'Annual Checkup', revenue: '$125,000', patients: 450, growth: '+12%' },
    { name: 'Physical Therapy', revenue: '$98,000', patients: 320, growth: '+8%' },
    { name: 'Dental Cleaning', revenue: '$87,000', patients: 280, growth: '+15%' },
    { name: 'Eye Exam', revenue: '$76,000', patients: 240, growth: '+6%' },
    { name: 'Consultation', revenue: '$65,000', patients: 200, growth: '+10%' }
  ];

  const recentTrends = [
    { metric: 'Patient Satisfaction', value: '4.8/5', change: '+0.2', trend: 'up' },
    { metric: 'Average Wait Time', value: '12 min', change: '-3 min', trend: 'down' },
    { metric: 'No-Show Rate', value: '8.5%', change: '-1.2%', trend: 'down' },
    { metric: 'Online Bookings', value: '73%', change: '+5%', trend: 'up' }
  ];

  const maxRevenue = Math.max(...chartData.map(d => d.revenue));
  const maxPatients = Math.max(...chartData.map(d => d.patients));
  const maxAppointments = Math.max(...chartData.map(d => d.appointments));

  return (
    <div className="analytics-container">
      {/* Metrics Overview */}
      <div className="metrics-grid">
        {metrics.map((metric, index) => (
          <div key={index} className="metric-card">
            <div className="metric-header">
              <div className="metric-icon" style={{ color: metric.color }}>
                {metric.icon}
              </div>
              <div className="metric-trend">
                {metric.trend === 'up' ? (
                  <TrendingUp size={16} color="#28a745" />
                ) : (
                  <TrendingDown size={16} color="#dc3545" />
                )}
              </div>
            </div>
            <div className="metric-content">
              <h3>{metric.value}</h3>
              <p>{metric.title}</p>
              <span 
                className={`metric-change ${metric.trend}`}
                style={{ color: metric.trend === 'up' ? '#28a745' : '#dc3545' }}
              >
                {metric.change}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts and Data */}
      <div className="analytics-content">
        {/* Revenue Chart */}
        <div className="chart-section">
          <div className="section-header">
            <h2>Revenue Trend</h2>
            <div className="chart-legend">
              <span className="legend-item">
                <span className="legend-color" style={{ backgroundColor: '#DAA520' }}></span>
                Revenue
              </span>
            </div>
          </div>
          <div className="chart-container">
            <div className="chart-bars">
              {chartData.map((data, index) => (
                <div key={index} className="chart-bar-group">
                  <div 
                    className="chart-bar"
                    style={{ 
                      height: `${(data.revenue / maxRevenue) * 200}px`,
                      backgroundColor: '#DAA520'
                    }}
                  ></div>
                  <span className="chart-label">{data.month}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Services */}
        <div className="services-section">
          <div className="section-header">
            <h2>Top Services</h2>
            <button className="btn btn-outline">View All</button>
          </div>
          <div className="services-list">
            {topServices.map((service, index) => (
              <div key={index} className="service-item">
                <div className="service-info">
                  <h4>{service.name}</h4>
                  <p>{service.patients} patients</p>
                </div>
                <div className="service-stats">
                  <span className="service-revenue">{service.revenue}</span>
                  <span className="service-growth">{service.growth}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Trends */}
        <div className="trends-section">
          <div className="section-header">
            <h2>Recent Trends</h2>
          </div>
          <div className="trends-list">
            {recentTrends.map((trend, index) => (
              <div key={index} className="trend-item">
                <div className="trend-info">
                  <h4>{trend.metric}</h4>
                  <span className="trend-value">{trend.value}</span>
                </div>
                <div className="trend-change">
                  {trend.trend === 'up' ? (
                    <TrendingUp size={16} color="#28a745" />
                  ) : (
                    <TrendingDown size={16} color="#dc3545" />
                  )}
                  <span 
                    className={`trend-amount ${trend.trend}`}
                    style={{ color: trend.trend === 'up' ? '#28a745' : '#dc3545' }}
                  >
                    {trend.change}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Patient Growth Chart */}
        <div className="growth-section">
          <div className="section-header">
            <h2>Patient Growth</h2>
          </div>
          <div className="growth-chart">
            <div className="chart-line">
              {chartData.map((data, index) => (
                <div key={index} className="chart-point-group">
                  <div 
                    className="chart-point"
                    style={{ 
                      bottom: `${(data.patients / maxPatients) * 200}px`,
                      backgroundColor: '#17a2b8'
                    }}
                  ></div>
                  <span className="chart-label">{data.month}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsCard;
